import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="17251" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/10 animate-pulse rounded-sm" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex max-w-full">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative block py-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-foreground/70 transition-colors hover:text-primary font-sans">
                {item.label}
                <span className="absolute -bottom-1 left-0 h-[2px] w-full origin-right scale-x-0 bg-primary transition-transform duration-500 ease-out group-hover:origin-left group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return null;
  return <div className="flex flex-col gap-10 p-10">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-4xl font-serif font-medium tracking-tight text-foreground/80 hover:text-primary transition-all duration-300 hover:translate-x-2" onClick={onClose}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="agency-header" className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out dark text-foreground ${isScrolled ? "bg-background/95 backdrop-blur-lg border-b border-primary/10 py-3 shadow-xl" : "bg-transparent py-6"}`}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        
        <Link to="/HomePage" className="relative z-50 flex items-center gap-3">
          <img src="https://deep-pup.10web.cloud/wp-content/uploads/2026/03/image_evl6fyfx.png" alt="FTG Financial Logo" className="object-contain transition-transform duration-500 !w-[100px] !h-[50px] hover:scale-110" />
        </Link>

        
        <div className="hidden md:block">
          <MenuProvider menu_id="47">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        
        <div className="flex items-center gap-4">
          <Button asChild className="hidden md:inline-flex relative overflow-hidden rounded-full bg-gradient-to-br from-primary via-primary to-orange-400 px-10 py-6 text-primary-foreground font-bold tracking-widest uppercase text-[11px] shadow-xl shadow-primary/20 hover:shadow-primary/40 hover:-translate-y-1 transition-all duration-500 group">
            <Link to="/ContactPage">
              <span className="relative z-10">Strategy Session</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />
            </Link>
          </Button>

          
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden relative z-50 text-foreground hover:text-primary transition-all active:scale-90" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-7" /> : <Menu className="size-7" />}
          </Button>
        </div>
      </div>

      
      <div className={`fixed inset-0 z-40 bg-background/95 backdrop-blur-3xl transition-all duration-700 ease-in-out md:hidden ${isMobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"}`} style={{
      top: "0px"
    }}>
        <div className="flex flex-col h-full pt-32 px-6">
          <MenuProvider menu_id="47">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
          
          <div className="mt-auto mb-16 px-10">
             <Button asChild className="w-full rounded-full bg-gradient-to-br from-primary to-orange-400 py-7 text-primary-foreground font-bold tracking-widest uppercase text-xs shadow-2xl shadow-primary/30 active:scale-95 transition-transform">
              <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)}>
                Strategy Session
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </header>;
}